import React, { useEffect } from 'react';
import './Modal.scss';

interface ModalProps {
  isOpen: boolean;
  title: string;
  /** Body content (form / list / anything). Whatever the caller wants. */
  children: React.ReactNode;
  /** Optional bottom-row content — usually action buttons. Kept as
   *  a slot so the caller controls the exact button layout instead
   *  of the modal shell hardcoding an OK/Cancel pair. */
  footer?: React.ReactNode;
  /** Fires on backdrop click, ESC key, or the built-in close (X)
   *  button. Gated when `isProcessing` is true. */
  onClose: () => void;
  /** Optional CSS-width class for the shell (`sm` / `md` / `lg`).
   *  Defaults to `md` = 480px, form-comfortable but tighter than
   *  the old 560px to feel less overwhelming on desktop. Both sizes
   *  are capped to a mobile-friendly width by the .klyx-modal
   *  mobile media query. */
  size?: 'sm' | 'md' | 'lg';
  /** Extra class on the modal-content div so callers can theme
   *  or size beyond the built-in size options. */
  className?: string;
  /** When true, backdrop click, ESC key, and the X button all
   *  ignore user close attempts. Use for destructive/irreversible
   *  operations mid-flight (e.g. Delete confirm dialogs while the
   *  DELETE request is in progress) so a stray click can't abandon
   *  a running action.
   *
   *  Form modals (Add endpoint / Add wallet) SHOULD NOT pass this —
   *  discarding an in-flight add is safe (the failed dispatch just
   *  no-ops).
   */
  isProcessing?: boolean;
}

/**
 * Generic form-friendly modal shell. Renders the shared .modal-overlay
 * + .modal-content skeleton so callers only need to write the header
 * text + body + footer.
 *
 * Deliberately simpler than ConfirmDialog:
 *   - No built-in Confirm/Cancel buttons — form modals have their
 *     own submit + cancel layouts. Use the `footer` slot.
 *   - No isProcessing prop — form modals should let the user cancel
 *     mid-flight; state cleanup is the caller's concern.
 *   - Closes on ESC (window-level listener while open) + backdrop
 *     click + the X button in the header.
 *
 * Renders nothing when isOpen is false — no portal, no hidden DOM.
 * If the app grows enough to need portals we can swap this later
 * without changing call sites.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  children,
  footer,
  onClose,
  size = 'md',
  className = '',
  isProcessing = false,
}) => {
  // Central close handler — every path (backdrop click, ESC, X)
  // routes through here so the isProcessing gate is consistent.
  const requestClose = () => {
    if (isProcessing) return;
    onClose();
  };

  // ESC-to-close. Effect must be inside the component body (not
  // gated behind isOpen at the definition level) so React sees a
  // consistent hook order across renders. Guarded internally on
  // isOpen so we only attach the listener while visible.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isProcessing, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={requestClose}>
      <div
        // Class contract:
        //   .modal-content    — shared shell (padding, radius, shadow) from
        //                       the app-wide modal base in PostSpotlightButton.scss.
        //                       Also declares max-width: 90vw as a floor cap.
        //   .klyx-modal       — this component's theme-token overrides
        //                       (background, border) so it survives light mode.
        //   .klyx-modal--{size} — width cap variant (sm/md/lg). The size class
        //                       MUST use the `klyx-modal--` prefix to match the
        //                       Modal.scss rules; earlier version had a
        //                       `modal-content--${size}` typo which meant the
        //                       cap never applied and content-driven modals
        //                       stretched past their intended max-width.
        className={`modal-content klyx-modal klyx-modal--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="klyx-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header klyx-modal__header">
          <h2 id="klyx-modal-title">{title}</h2>
          <button
            type="button"
            className="btn-close klyx-modal__close"
            onClick={requestClose}
            aria-label="Close"
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        <div className="modal-body klyx-modal__body">{children}</div>

        {footer && (
          <div className="modal-footer klyx-modal__footer">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default Modal;
